import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { parseLrc } from "@/lib/lrc";
import { CARD_SIZES, renderCard, ensureCardFonts, groupSelected } from "@/lib/shareCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Download, Share2, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const STYLES = [
  { id: "cover", label: "Cover blur" },
  { id: "dark", label: "Solid dark" },
  { id: "gradient", label: "Gradient" },
];

function useCoverImage(song, open) {
  const [img, setImg] = useState(null);
  useEffect(() => {
    if (!open || !song?.has_cover) return setImg(null);
    let url;
    let alive = true;
    api
      .get(`/songs/${song.id}/cover`, { responseType: "blob", params: { v: song.cover_updated_at } })
      .then(({ data }) => {
        url = URL.createObjectURL(data);
        const im = new Image();
        im.onload = () => alive && setImg(im);
        im.src = url;
      })
      .catch(() => setImg(null));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, song?.id, song?.has_cover, song?.cover_updated_at]);
  return img;
}

function Pill({ active, onClick, children, testId, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors disabled:opacity-30 ${
        active
          ? "bg-[#FF3B30] border-[#FF3B30] text-white"
          : "bg-transparent border-white/10 text-white/60 hover:text-white hover:border-white/30"
      }`}
    >
      {children}
    </button>
  );
}

function LinePicker({ lines, selected, toggle }) {
  return (
    <div className="space-y-1 overflow-y-auto no-scrollbar max-h-[30vh] lg:max-h-[58vh] pr-1" data-testid="share-line-list">
      {lines.map((l, i) => {
        const on = selected.has(i);
        const empty = !l.text;
        return (
          <button
            key={i}
            type="button"
            disabled={empty}
            onClick={() => toggle(i)}
            data-testid={`share-line-${i}`}
            className={`w-full flex items-start gap-3 text-left px-3 py-2 rounded-sm border transition-colors ${
              on
                ? "border-[#FF3B30]/60 bg-[#FF3B30]/10 text-white"
                : "border-transparent hover:bg-white/[0.04] text-white/60 hover:text-white"
            } ${empty ? "opacity-20 cursor-default" : ""}`}
          >
            <span
              className={`mt-0.5 h-4 w-4 shrink-0 rounded-[3px] border flex items-center justify-center ${
                on ? "bg-[#FF3B30] border-[#FF3B30]" : "border-white/25"
              }`}
            >
              {on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
            </span>
            <span className="font-display font-semibold text-sm leading-snug">{l.text || "♪"}</span>
          </button>
        );
      })}
    </div>
  );
}

async function canvasToFile(canvas, name) {
  const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
  return new File([blob], name, { type: "image/png" });
}

function downloadFile(file) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ShareLyricsDialog({ open, onOpenChange, song, initialIndex = -1 }) {
  const { lines } = useMemo(() => parseLrc(song?.lyrics || ""), [song?.lyrics]);
  const [selected, setSelected] = useState(new Set());
  const [style, setStyle] = useState("cover");
  const [size, setSize] = useState("square");
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const img = useCoverImage(song, open);

  useEffect(() => {
    if (!open) return;
    setSelected(initialIndex >= 0 && lines[initialIndex]?.text ? new Set([initialIndex]) : new Set());
    setStyle(song?.has_cover ? "cover" : "gradient");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const groups = useMemo(() => groupSelected(lines, selected), [lines, selected]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    ensureCardFonts().then(() => {
      if (!alive || !canvasRef.current) return;
      renderCard(canvasRef.current, {
        groups: groups.length ? groups : [["Pick a line to share"]],
        title: song?.title,
        artist: song?.artist,
        img,
        style: style === "cover" && !img ? "dark" : style,
        size,
      });
    });
    return () => {
      alive = false;
    };
  }, [open, groups, img, style, size, song?.title, song?.artist]);

  const toggle = (i) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });

  const fileName = `${(song?.title || "lyrics").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-lyriq.png`;

  const onDownload = async () => {
    setBusy(true);
    try {
      downloadFile(await canvasToFile(canvasRef.current, fileName));
      toast.success("Card downloaded");
    } finally {
      setBusy(false);
    }
  };

  const onShare = async () => {
    setBusy(true);
    try {
      const file = await canvasToFile(canvasRef.current, fileName);
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: song?.title, text: `${song?.title} — ${song?.artist || ""}` });
      } else {
        downloadFile(file);
        toast.message("Sharing isn't supported here — card downloaded instead");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't share the card");
    } finally {
      setBusy(false);
    }
  };

  const ready = selected.size > 0 && !busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto bg-[#0F0F0F] border-white/10 text-white rounded-md p-5 md:p-7"
        data-testid="share-lyrics-dialog"
      >
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">// share card</div>
          <DialogTitle className="font-display font-black text-3xl tracking-tighter">Share lyrics</DialogTitle>
          <DialogDescription className="text-white/50 text-sm">
            Tap one or more lines — they don't need to be next to each other.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
          <div className="space-y-3 min-w-0">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50" data-testid="share-selected-count">
                {selected.size} line{selected.size === 1 ? "" : "s"} selected
              </span>
              {selected.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white"
                  data-testid="share-clear-btn"
                >
                  <X className="h-3 w-3" /> clear
                </button>
              )}
            </div>
            <LinePicker lines={lines} selected={selected} toggle={toggle} />
          </div>

          <div className="space-y-4 min-w-0">
            <div className="flex flex-wrap gap-2" data-testid="share-style-options">
              {STYLES.map((s) => (
                <Pill
                  key={s.id}
                  active={style === s.id}
                  onClick={() => setStyle(s.id)}
                  disabled={s.id === "cover" && !song?.has_cover}
                  testId={`share-style-${s.id}`}
                >
                  {s.label}
                </Pill>
              ))}
              <span className="w-px bg-white/10 mx-1" />
              {Object.entries(CARD_SIZES).map(([k, v]) => (
                <Pill key={k} active={size === k} onClick={() => setSize(k)} testId={`share-size-${k}`}>
                  {v.label}
                </Pill>
              ))}
            </div>
            <div className="flex justify-center bg-black/40 border border-white/5 rounded-sm p-3">
              <canvas
                ref={canvasRef}
                className={`rounded-sm shadow-[0_20px_60px_rgba(0,0,0,0.6)] ${
                  size === "story" ? "h-[46vh] w-auto" : "w-full max-w-[420px] h-auto"
                }`}
                data-testid="share-card-preview"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={onShare}
                disabled={!ready}
                className="flex-1 bg-[#FF3B30] hover:bg-[#FF5C53] text-white rounded-sm font-display font-bold uppercase text-xs tracking-wide"
                data-testid="share-card-share-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Share2 className="h-4 w-4 mr-2" />}
                Share
              </Button>
              <Button
                onClick={onDownload}
                disabled={!ready}
                variant="outline"
                className="flex-1 bg-transparent border-white/10 text-white hover:bg-[#1A1A1A] hover:text-white rounded-sm font-mono uppercase text-xs tracking-[0.2em]"
                data-testid="share-card-download-btn"
              >
                <Download className="h-4 w-4 mr-2" /> Download PNG
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
